import { Edit } from '@mui/icons-material';
import { Box, Button, IconButton } from '@mui/material';
import React, { ChangeEvent, ReactNode, useEffect, useId, useState } from "react";
import { useStrings } from "../../common-strings";
import { DataSnapshot } from "../../api/firebase";
import { profileFieldAnchorId } from "../validation";
import { useUserProfileStrings } from "./userProfile-strings";

type ProfileEditableFieldProps = {
    name: string;
    label: ReactNode;
    snapshot: DataSnapshot;
    activeEditField: string | null;
    onEditFieldChange: (fieldName: string | null) => void;
    onSaved?: (value: string) => void;
    /** When set, Save is disabled while trimmed draft is empty; click still guarded with this message. */
    requiredEmptyMessage?: string;
    /** Figma personal-info card field layout (label + value, teal edit). */
    variant?: 'default' | 'figma';
    /** Profile completeness validation highlights this field. */
    validationInvalid?: boolean;
    /** Shown in red on the field when `validationInvalid` (not in the top banner). */
    validationErrorMessage?: string;
};

export function ProfileEditableField({ name, label, snapshot, activeEditField, onEditFieldChange, onSaved, requiredEmptyMessage, variant = 'default', validationInvalid, validationErrorMessage }: ProfileEditableFieldProps) {
    const commonStrings = useStrings();
    const profileStrings = useUserProfileStrings();
    const rawStored = snapshot.child(name).val();
    const storedValue = rawStored == null ? '' : String(rawStored);
    const displayValue = storedValue.trim();
    const editing = activeEditField === name;
    const [draftValue, setDraftValue] = useState(storedValue);
    const [saveError, setSaveError] = useState('');
    const fieldInputId = useId();
    const fieldLabelId = `${fieldInputId}-label`;

    useEffect(() => {
        if (!editing) {
            const next = snapshot.child(name).val();
            setDraftValue(next == null ? '' : String(next));
        }
    }, [editing, name, snapshot]);

    const startEditing = (): void => {
        const next = snapshot.child(name).val();
        setDraftValue(next == null ? '' : String(next));
        setSaveError('');
        onEditFieldChange(name);
    };

    const handleCancel = (): void => {
        const next = snapshot.child(name).val();
        setDraftValue(next == null ? '' : String(next));
        setSaveError('');
        onEditFieldChange(null);
    };

    const handleSave = (): void => {
        const value = draftValue.trim();
        if (requiredEmptyMessage && !value) {
            setSaveError(requiredEmptyMessage);
            return;
        }
        snapshot.ref.child(name).set(value);
        onSaved?.(value);
        setSaveError('');
        onEditFieldChange(null);
    };

    const handleDraftChange = ({ target: { value } }: ChangeEvent<HTMLInputElement>): void => {
        setDraftValue(value);
        if (saveError) {
            setSaveError('');
        }
    };

    const trimmedDraft = draftValue.trim();
    const saveDisabled = !!requiredEmptyMessage && !trimmedDraft;
    /** While editing, inline validation matches Save: disabled → show error, enabled → hide. */
    const showValidationFeedback = validationInvalid && (!editing || saveDisabled);
    const fieldAnchorId = profileFieldAnchorId(name as 'firstName' | 'lastName' | 'email' | 'mobile');
    const fieldValidationErrorId = `${fieldAnchorId}-validation-error`;
    const fieldLabelText = typeof label === 'string' ? label : name;
    const editAriaLabel = variant === 'figma' ? profileStrings.editFieldAria(fieldLabelText) : commonStrings.change;
    const showRequiredLabel = name === 'firstName' || name === 'lastName' || name === 'mobile';

    if (variant === 'figma') {
        return <>
            <div
                id={fieldAnchorId}
                tabIndex={-1}
                className={`account-figma-field${editing ? ' account-figma-field--editing' : ''}${showValidationFeedback ? ' account-figma-field--validation-error' : ''}`}
                aria-invalid={showValidationFeedback ? true : undefined}
                aria-describedby={showValidationFeedback ? fieldValidationErrorId : undefined}
            >
                <div className="account-figma-field__label-row">
                    <span id={fieldLabelId} className={`account-figma-field__label${showRequiredLabel ? ' account-figma-field__label--required' : ''}`}>{label}</span>
                </div>
                {editing ? (
                    <input
                        id={fieldInputId}
                        className="account-figma-field__input"
                        name={name}
                        type={name === 'mobile' ? 'tel' : 'text'}
                        inputMode={name === 'mobile' ? 'tel' : undefined}
                        autoComplete={name === 'mobile' ? 'tel' : undefined}
                        placeholder={name === 'mobile' ? profileStrings.mobileInputPlaceholder : undefined}
                        value={draftValue}
                        onChange={handleDraftChange}
                        aria-labelledby={fieldLabelId}
                        required={showRequiredLabel}
                        autoFocus
                    />
                ) : (
                    <div
                        id={fieldInputId}
                        className={`account-figma-field__value${!displayValue ? ' account-figma-field__value--empty' : ''}`}
                        aria-labelledby={fieldLabelId}
                    >
                        {displayValue || profileStrings.profileFieldEmpty}
                    </div>
                )}
                {!editing && (
                    <span className="account-figma-field__label-actions">
                        <IconButton
                            size="small"
                            className="account-figma-field__edit"
                            onClick={startEditing}
                            aria-label={editAriaLabel}
                        >
                            <img src="/img-assets/edit-orange.svg" alt="" className="account-figma-field__edit-img" />
                        </IconButton>
                    </span>
                )}
                {showValidationFeedback && validationErrorMessage && (
                    <p id={fieldValidationErrorId} className="account-figma-field__validation-error" role="alert">
                        {validationErrorMessage}
                    </p>
                )}
                {saveError && (!editing || saveDisabled) && (
                    <Box role="alert" sx={{ color: 'var(--red_error)', fontSize: '0.875rem', mt: 0.5 }}>
                        {saveError}
                    </Box>
                )}
                {editing && (
                    <Box className="profile-field-actions" sx={{ display: 'flex', gap: 0.5, mt: 1, alignItems: 'center', justifyContent: 'flex-end' }}>
                        <Button
                            size="small"
                            color="primary"
                            onClick={handleCancel}
                        >
                            {commonStrings.cancel2}
                        </Button>
                        <Button
                            size="small"
                            variant="contained"
                            disableElevation
                            disabled={saveDisabled}
                            onClick={handleSave}
                        >
                            {commonStrings.save}
                        </Button>
                    </Box>
                )}
            </div>
            <div className='separator account-figma-card__divider'></div>
        </>;
    }

    return <>
        <label className='content'>
            <div className='input-label' style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {label}
                {!editing && (
                    <IconButton
                        size="small"
                        onClick={startEditing}
                        aria-label={commonStrings.change}
                        sx={{
                            padding: '4px',
                            color: 'var(--lb_orange)',
                            '&:hover': {
                                backgroundColor: 'rgba(25, 118, 210, 0.04)',
                            },
                        }}
                    >
                        <Edit sx={{ fontSize: '16px' }} />
                    </IconButton>
                )}
            </div>
            <input
                name={name}
                style={{ opacity: editing ? undefined : '0.5' }}
                readOnly={!editing}
                disabled={!editing}
                value={editing ? draftValue : storedValue}
                onChange={handleDraftChange}
            />
            {saveError && (
                <Box sx={{ color: 'var(--red_error)', fontSize: '0.875rem', mt: 0.5 }}>
                    {saveError}
                </Box>
            )}
            {editing && (
                <Box className="profile-field-actions" sx={{ display: 'flex', gap: 0.5, mt: 1, alignItems: 'center', justifyContent: 'flex-end' }}>
                    <Button
                        size="small"
                        color="primary"
                        onClick={handleCancel}
                    >
                        {commonStrings.cancel2}
                    </Button>
                    <Button
                        size="small"
                        variant="contained"
                        disableElevation
                        disabled={saveDisabled}
                        onClick={handleSave}
                    >
                        {commonStrings.save}
                    </Button>
                </Box>
            )}
        </label>
        <div className='separator'></div>
    </>;
}

export function Input({ name, label, snapshot, disabled = false, variant = 'default', value: valueProp, labelRequired = false, ...rest }: InputProps) {
    const snapshotValue = snapshot.child(name).val() ?? '';
    const value = valueProp !== undefined ? valueProp : snapshotValue;
    const inputId = useId();

    if (variant === 'figmaDialog') {
        return (
            <div className="student-person-edit-dialog__field-label-wrap">
                <label htmlFor={inputId} className={`account-figma-field__label${labelRequired ? ' account-figma-field__label--required' : ''}`}>{label}</label>
                <input
                    id={inputId}
                    name={name}
                    className={`account-figma-field__input${disabled ? ' student-person-edit-dialog__input--disabled' : ''}`}
                    disabled={disabled}
                    readOnly={disabled}
                    required={labelRequired && !disabled}
                    value={value}
                    {...rest}
                />
            </div>
        );
    }

    return <>
        <label className='content'>
            <div className='input-label'>{label}</div>
            <input name={name} style={{ opacity: disabled ? '0.5' : '' }} disabled={disabled} value={value} {...rest} />
        </label>
        <div className='separator'></div>
    </>
}



type InputProps = {
    name: string
    label: ReactNode
    snapshot: DataSnapshot
    variant?: 'default' | 'figmaDialog'
    /** Red asterisk on figma dialog label (e.g. student name fields). */
    labelRequired?: boolean
} & React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>